(function () {
  'use strict';

  const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function clean(value) {
    return String(value || '').trim().replace(/\s+/g, ' ');
  }

  function normalizedGuess(value) {
    return clean(value).toLocaleLowerCase();
  }

  function errorMessage(status, body) {
    if (status === 409) return 'An entry has already been submitted with this email address.';
    if (status === 429) return 'Too many attempts were made. Wait a few minutes and try again.';
    if (body && body.error) return body.error;
    return 'Your entry could not be submitted. Please try again.';
  }

  function initializeCountdown(root) {
    const countdown = root.querySelector('[data-first-reveal-countdown]');
    const target = Date.parse(root.dataset.firstRevealAt || '');
    if (!countdown || Number.isNaN(target)) return;

    const grid = countdown.querySelector('[data-countdown-grid]');
    const complete = countdown.querySelector('[data-countdown-complete]');
    const days = countdown.querySelector('[data-countdown-days]');
    const hours = countdown.querySelector('[data-countdown-hours]');
    const minutes = countdown.querySelector('[data-countdown-minutes]');
    const seconds = countdown.querySelector('[data-countdown-seconds]');
    if (!grid || !complete || !days || !hours || !minutes || !seconds) return;

    let countdownInterval = 0;

    function update() {
      const remaining = Math.max(0, target - Date.now());
      const totalSeconds = Math.floor(remaining / 1000);
      const values = {
        days: Math.floor(totalSeconds / 86400),
        hours: Math.floor((totalSeconds % 86400) / 3600),
        minutes: Math.floor((totalSeconds % 3600) / 60),
        seconds: totalSeconds % 60
      };

      days.textContent = String(values.days).padStart(2, '0');
      hours.textContent = String(values.hours).padStart(2, '0');
      minutes.textContent = String(values.minutes).padStart(2, '0');
      seconds.textContent = String(values.seconds).padStart(2, '0');
      countdown.setAttribute(
        'aria-label',
        `${values.days} days, ${values.hours} hours, ${values.minutes} minutes, ${values.seconds} seconds until the third reveal`
      );

      if (remaining === 0) {
        grid.hidden = true;
        complete.hidden = false;
        countdown.classList.add('is-complete');
        window.clearInterval(countdownInterval);
      }
    }

    update();
    if (Date.now() < target) countdownInterval = window.setInterval(update, 1000);
  }

  function initialize(root) {
    if (!root || root.dataset.initialized === 'true') return;

    const form = root.querySelector('[data-contest-form]');
    const success = root.querySelector('[data-contest-success]');
    const formError = root.querySelector('[data-form-error]');
    const submitButton = root.querySelector('[data-submit-button]');
    const submitLabel = root.querySelector('[data-submit-label]');
    const successName = root.querySelector('[data-success-name]');
    const successEmail = root.querySelector('[data-success-email]');
    const successGuesses = root.querySelector('[data-success-guesses]');
    const previewMode = root.dataset.previewMode === 'true';

    if (
      !form ||
      !success ||
      !formError ||
      !submitButton ||
      !submitLabel ||
      !successName ||
      !successEmail ||
      !successGuesses
    )
      return;

    root.dataset.initialized = 'true';
    initializeCountdown(root);
    const defaultSubmitLabel = submitLabel.textContent.trim();

    const country = form.elements.namedItem('country_code');
    if (country && window.Shopify && ['CA', 'US'].includes(window.Shopify.country)) {
      country.value = window.Shopify.country;
    }

    function field(name) {
      return form.elements.namedItem(name);
    }

    function fieldError(name) {
      return form.querySelector(`[data-error-for="${name}"]`);
    }

    function clearErrors() {
      formError.hidden = true;
      formError.textContent = '';
      form.querySelectorAll('[aria-invalid="true"]').forEach((input) => input.removeAttribute('aria-invalid'));
      form.querySelectorAll('[data-error-for]').forEach((node) => {
        node.textContent = '';
      });
    }

    function setError(name, message) {
      const input = field(name);
      const output = fieldError(name);
      if (input) input.setAttribute('aria-invalid', 'true');
      if (output) output.textContent = message;
    }

    function validate() {
      clearErrors();
      let valid = true;
      let firstInvalid = null;

      ['guess_1', 'guess_2', 'guess_3', 'first_name', 'last_name', 'email', 'country_code'].forEach((name) => {
        const input = field(name);
        if (!input || !clean(input.value)) {
          setError(name, name.startsWith('guess_') ? 'Enter a player name.' : 'This field is required.');
          firstInvalid = firstInvalid || input;
          valid = false;
        }
      });

      const email = field('email');
      if (email && clean(email.value) && !EMAIL_PATTERN.test(clean(email.value))) {
        setError('email', 'Enter a valid email address.');
        firstInvalid = firstInvalid || email;
        valid = false;
      }

      const guesses = ['guess_1', 'guess_2', 'guess_3'].map((name) => normalizedGuess(field(name)?.value));
      guesses.forEach((guess, index) => {
        if (guess && guesses.indexOf(guess) !== index) {
          const name = `guess_${index + 1}`;
          setError(name, 'Use a different player for every pick.');
          firstInvalid = firstInvalid || field(name);
          valid = false;
        }
      });

      const rules = field('rules_accepted');
      if (!rules || !rules.checked) {
        setError('rules_accepted', 'Accept the Official Rules to continue.');
        firstInvalid = firstInvalid || rules;
        valid = false;
      }

      if (!valid) {
        formError.textContent = 'Check the highlighted fields before submitting.';
        formError.hidden = false;
        if (firstInvalid) firstInvalid.focus();
      }

      return valid;
    }

    function payload() {
      return {
        campaign_slug: clean(root.dataset.campaign),
        first_name: clean(field('first_name').value),
        last_name: clean(field('last_name').value),
        email: clean(field('email').value).toLocaleLowerCase(),
        country_code: clean(field('country_code').value).toUpperCase(),
        guesses: ['guess_1', 'guess_2', 'guess_3'].map((name) => clean(field(name).value)),
        rules_accepted: Boolean(field('rules_accepted').checked),
        marketing_opt_in: Boolean(field('marketing_opt_in')?.checked),
        company: clean(field('company').value),
        source_page: `${window.location.origin}${window.location.pathname}`
      };
    }

    function showSuccess(entry) {
      successName.textContent = clean(`${entry.first_name} ${entry.last_name}`);
      successEmail.textContent = entry.email;
      successGuesses.replaceChildren();
      entry.guesses.forEach((guess) => {
        const item = document.createElement('li');
        const value = document.createElement('strong');
        value.textContent = guess;
        item.appendChild(value);
        successGuesses.appendChild(item);
      });

      if (previewMode) {
        const heading = success.querySelector('h2');
        const message = success.querySelector('h2 + p');
        const eyebrow = success.querySelector('.superstar-contest__eyebrow');
        if (eyebrow) eyebrow.textContent = 'Preview complete';
        if (heading) heading.textContent = 'Your preview looks good.';
        if (message) message.textContent = 'Nothing has been saved. Connect the Railway API before opening entries.';
      }

      form.hidden = true;
      const previewNotice = root.querySelector('.superstar-contest__preview-notice');
      if (previewNotice) previewNotice.hidden = true;
      success.hidden = false;
      success.focus();
    }

    function endpointUrl() {
      const endpoint = clean(root.dataset.apiEndpoint).replace(/\/$/, '');
      if (!endpoint) return '';
      return endpoint.endsWith('/api/entries') ? endpoint : `${endpoint}/api/entries`;
    }

    form.addEventListener('input', (event) => {
      const input = event.target;
      if (!input || !input.name) return;
      input.removeAttribute('aria-invalid');
      const output = fieldError(input.name);
      if (output) output.textContent = '';
      formError.hidden = true;
    });

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      if (!validate()) return;

      const entry = payload();
      submitButton.disabled = true;
      submitLabel.textContent = previewMode ? 'Checking entry...' : 'Submitting...';

      try {
        if (previewMode) {
          await new Promise((resolve) => window.setTimeout(resolve, 350));
          showSuccess(entry);
          return;
        }

        const response = await fetch(endpointUrl(), {
          method: 'POST',
          mode: 'cors',
          credentials: 'omit',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(entry)
        });

        let body = null;
        try {
          body = await response.json();
        } catch (_error) {
          body = null;
        }

        if (!response.ok) throw new Error(errorMessage(response.status, body));
        showSuccess(entry);
      } catch (error) {
        formError.textContent = error instanceof Error ? error.message : 'Your entry could not be submitted.';
        formError.hidden = false;
        formError.focus();
      } finally {
        submitButton.disabled = false;
        submitLabel.textContent = defaultSubmitLabel;
      }
    });
  }

  function initializeAll(container) {
    const scope = container || document;
    if (scope.matches && scope.matches('[data-superstar-contest]')) initialize(scope);
    scope.querySelectorAll('[data-superstar-contest]').forEach(initialize);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initializeAll(document));
  } else {
    initializeAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => initializeAll(event.target));
})();
